import React from 'react';
import { Box, Flex, Text, SimpleGrid, VStack, HStack } from '@chakra-ui/react';
import { BackButton } from '../components/TopSection/ExportButton';

type DataItem = {
  label: string;
  value: number;
};

type Props = {
  data: DataItem[];
  max?: number;
  color?: string;
};

export const RadarChart = ({ data, max = 100, color = '#319795' }: Props) => {
  const size = 220;
  const center = size / 2;
  const padding = 35;
  const radius = center - padding;

  const angleStep = (Math.PI * 2) / data.length;

  // pontos do polígono
  const points = data
    .map((item, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const r = (item.value / max) * radius;

      const x = center + Math.cos(angle) * r;
      const y = center + Math.sin(angle) * r;

      return `${x},${y}`;
    })
    .join(' ');

  // linhas de grid (círculos)
  const gridLevels = 4;

  return (
    <Box w="100%" h="220px">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        width="100%"
        height="100%"
        style={{ overflow: 'visible' }}
      >
        {/* GRID */}
        {[...Array(gridLevels)].map((_, i) => {
          const r = ((i + 1) / gridLevels) * radius;
          return (
            <circle
              cx={center}
              cy={center}
              r={r}
              fill="none"
              stroke="#E2E8F0"
            />
          );
        })}

        {/* LINHAS RADIAIS */}
        {data.map((_, i) => {
          const angle = i * angleStep - Math.PI / 2;

          const x = center + Math.cos(angle) * radius;
          const y = center + Math.sin(angle) * radius;

          return (
            <line x1={center} y1={center} x2={x} y2={y} stroke="#E2E8F0" />
          );
        })}

        {/* POLÍGONO */}
        <polygon
          points={points}
          fill={color}
          fillOpacity={0.3}
          stroke={color}
          strokeWidth={2}
        />

        {/* LABELS + VALORES */}
        {data.map((item, i) => {
          const angle = i * angleStep - Math.PI / 2;

          // label
          const labelX = center + Math.cos(angle) * (radius + 10);
          const labelY = center + Math.sin(angle) * (radius + 10);

          // valor
          const valueX = center + Math.cos(angle) * (radius + 25);
          const valueY = center + Math.sin(angle) * (radius + 25);

          return (
            <g>
              <text
                x={labelX}
                y={labelY}
                fontSize="11"
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#4A5568"
              >
                {item.label}
              </text>

              <text
                x={valueX}
                y={valueY}
                fontSize="13"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="middle"
                fill={color}
              >
                {item.value}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};

export const StatusItem = ({ label, value, color, highlight }: any) => {
  return (
    <Flex
      w="100%"
      justify="space-between"
      align="center"
      p={2}
      borderRadius="md"
      border={highlight ? '1px solid #F6AD55' : '1px solid transparent'}
      bg={highlight ? 'orange.50' : 'gray.50'}
    >
      <Text fontSize="sm">{label}</Text>

      <Text fontSize="sm" fontWeight="medium" color={color}>
        {value}
      </Text>
    </Flex>
  );
};

export default function EcomindPage({ OnBack }: any) {
  return (
    <Box p={10} bg="gray.100" position="relative">
      <Box position="absolute" top={10} marginBottom={20}>
        <BackButton OnBack={OnBack} />
      </Box>
      {/* HEADER */}
      <Box bg="white" p={5} borderRadius="lg" mb={6} mt={20}>
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <Box
              bg="blue.800"
              color="white"
              px={3}
              py={2}
              borderRadius="md"
              mr={3}
            >
              E
            </Box>

            <Box>
              <Text fontWeight="bold">EcoMind</Text>
              <Text fontSize="sm">Saneago — Saneamento de Goiás S.A.</Text>
              <Text fontSize="xs" color="gray.500">
                Ciclo Jan-Mar 2025
              </Text>
            </Box>
          </Flex>

          <HStack spacing={10}>
            <Box textAlign="center">
              <Text fontSize="xs">Colaboradores avaliados</Text>
              <Text fontWeight="bold" fontSize="2xl">
                847
              </Text>
            </Box>

            <Box textAlign="center">
              <Text fontSize="xs">Taxa de resposta</Text>
              <Text fontWeight="bold" fontSize="2xl">
                73%
              </Text>
            </Box>
          </HStack>
        </Flex>

        <Text mt={4} fontSize="xs" color="gray.500">
          Dados agregados. Visualização compatível com NR-1. Informações
          individuais não são exibidas neste painel.
        </Text>
      </Box>

      {/* PERFIL */}
      <Box bg="white" p={6} borderRadius="lg" mb={6}>
        <Text fontWeight="bold" mb={6} fontSize="md">
          Perfil Psicossocial Organizacional
        </Text>

        <SimpleGrid columns={[1, 1, 2]} spacing={8}>
          {/* Radar 1 */}
          <Box>
            <Text fontSize="sm" mb={3} textAlign="center">
              Tecnologia e Bem-Estar Digital (RED/TIC)
            </Text>

            <Flex justify="center" align="center" h="220px">
              <RadarChart
                data={[
                  { label: 'Descrença', value: 11 },
                  { label: 'Fadiga', value: 16 },
                  { label: 'Ansiedade', value: 9 },
                  { label: 'Ineficiência', value: 13 },
                ]}
                max={20}
                color="#2B6CB0"
              />
            </Flex>

            <VStack mt={5} spacing={2} align="stretch">
              <StatusItem label="Descrença" value="Leve" color="green.500" />
              <StatusItem label="Fadiga" value="Moderada" color="orange.400" />
              <StatusItem label="Ansiedade" value="Baixa" color="green.500" />
              <StatusItem label="Ineficiência" value="Leve" color="green.500" />
            </VStack>

            <Box bg="blue.50" p={3} mt={4} borderRadius="md">
              <Text fontSize="xs">
                {`"Fadiga digital é a dimensão com maior presença neste ciclo."`}
              </Text>
            </Box>
          </Box>

          {/* Radar 2 */}
          <Box>
            <Text fontSize="sm" mb={3} textAlign="center">
              Fatores Organizacionais (COPSOQ-II)
            </Text>

            <Flex justify="center" align="center" h="220px">
              <RadarChart
                data={[
                  { label: 'Exigências', value: 35 },
                  { label: 'Organização', value: 72 },
                  { label: 'Relações', value: 61 },
                  { label: 'Trab-Indiv', value: 79 },
                  { label: 'Valores', value: 66 },
                  { label: 'Saúde', value: 48 },
                ]}
              />
            </Flex>

            <VStack mt={5} spacing={2} align="stretch">
              <StatusItem label="Exigências" value="Baixo" color="green.500" />
              <StatusItem label="Organização" value="Alto" color="red.400" />
              <StatusItem label="Relações" value="Alto" color="red.400" />
              <StatusItem label="Trab-Indiv" value="Alto" color="red.400" />
              <StatusItem label="Valores" value="Alto" color="red.400" />
              <StatusItem
                label="Saúde e Bem-Estar"
                value="Desfavorável"
                color="orange.400"
              />
            </VStack>
          </Box>
        </SimpleGrid>
      </Box>

      {/* INSIGHTS */}
      <Box bg="white" p={5} borderRadius="lg" mb={6}>
        <Text fontWeight="bold" mb={4}>
          Insights Estratégicos
        </Text>

        <VStack align="start" spacing={4}>
          <Box borderLeft="3px solid #3182CE" pl={3}>
            <Text fontWeight="bold">Ponto de Atenção Principal</Text>
            <Text fontSize="sm">
              A fadiga digital apresenta-se como a dimensão mais elevada no
              módulo RED/TIC, sugerindo necessidade de revisão nas práticas de
              uso de tecnologia e ferramentas digitais pelos colaboradores.
            </Text>
          </Box>

          <Box borderLeft="3px solid #DD6B20" pl={3}>
            <Text fontWeight="bold">Saúde e Bem-Estar Organizacional</Text>
            <Text fontSize="sm">
              O indicador de Saúde e Bem-Estar (COPSOQ-III) encontra-se em nível
              moderado (48), requerendo iniciativas focadas em qualidade de vida
              no trabalho e suporte à saúde mental dos colaboradores.
            </Text>
          </Box>

          <Box borderLeft="3px solid #38A169" pl={3}>
            <Text fontWeight="bold">Aspectos Positivos</Text>
            <Text fontSize="sm">
              A organização demonstra bons resultados em Organização do Trabalho
              (72), Interface Trabalho-Indivíduo (79) e Valores no Local de
              Trabalho (66), indicando clima organizacional favorável e
              alinhamento cultural.
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* RECOMENDAÇÕES */}
      <Box bg="white" p={5} borderRadius="lg">
        <Text fontWeight="bold" mb={4}>
          Recomendações Executivas
        </Text>

        <VStack spacing={4}>
          {[
            {
              n: 1,
              title: 'Gestão da Fadiga Digital',
              desc: 'Implementar políticas de uso consciente de ferramentas digitais, pausas programadas e treinamentos sobre higiene digital para reduzir sobrecarga tecnológica.',
              color: 'orange.400',
            },
            {
              n: 2,
              title: 'Programa de Saúde e Bem-Estar',
              desc: 'Desenvolver programa estruturado de saúde mental e bem-estar, incluindo acesso a suporte psicológico, atividades de promoção da saúde e acompanhamento contínuo.',
              color: 'orange.400',
            },
            {
              n: 3,
              title: 'Monitoramento Contínuo',
              desc: 'Estabelecer ciclos trimestrais de avaliação para acompanhamento dos indicadores psicossociais e ajuste das intervenções conforme evolução dos resultados.',
              color: 'blue.500',
            },
          ].map(item => (
            <Flex
              key={item.n}
              p={4}
              w="100%"
              bg="gray.50"
              borderRadius="lg"
              align="center"
            >
              <Box
                bg={item.color}
                color="white"
                borderRadius="full"
                w="30px"
                h="30px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mr={3}
              >
                {item.n}
              </Box>

              <Box>
                <Text fontWeight="bold">{item.title}</Text>
                <Text fontSize="sm">{item.desc}</Text>
              </Box>
            </Flex>
          ))}
        </VStack>
      </Box>
    </Box>
  );
}
